import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

import { ALL_PRODUCT_TYPES } from "constants/chart.constants";
import { EvaChart } from "constants/charts.enums";
import { ProductNames } from "constants/forecasts.constants";
import { ICheckedForecast } from "store/features/app/appSlice";
import { RootState } from "store/rootReducer";
import { selectCheckedGlobalTypeWells, useForecastStore } from "stores/forecast";
import { getProductType } from "utils";

import { ArpSegment } from "models/UserArpsModel";

import { ToastType, showChartToast } from "./ChartToast";

interface TypeWellCutoffToastNotifierProps {
  chartId: string;
  currentProduct: string;
  chartType: EvaChart;
}

/**
 * Component that monitors type wells and shows a notification when:
 * - A new type well is added to the checked global type wells OR
 * - An existing type well's arps array changes (products added/removed/modified)
 * - The current chart product changes OR
 * - The chart type changes
 * - The current chart product matches a secondary product in the type well
 * - That secondary product's end date extends beyond the primary product's end date
 *
 * The primary product is defined as the first element in the arps array (arps[0]).
 * Secondary products are all subsequent elements (arps[1], arps[2], etc.).
 *
 * Notifications trigger on:
 * - New type wells being added
 * - Changes to a type well's arps array (e.g., adding/removing GAS product)
 * - Current product changes
 * - Chart type changes
 */
export default function TypeWellCutoffToastNotifier({
  chartId,
  currentProduct,
  chartType
}: TypeWellCutoffToastNotifierProps) {
  const checkedGlobalTypeWells = useForecastStore(selectCheckedGlobalTypeWells);
  const isMultiProductActive = useSelector(
    (state: RootState) =>
      state.charts?.chart?.[chartId]?.features?.multiProduct?.active ?? false
  );

  // Track previous type well IDs to detect newly added ones
  const previousTypeWellIdsRef = useRef<Set<string>>(new Set());
  // Track the arps array hash for each type well to detect changes
  const previousArpsHashRef = useRef<Map<string, string>>(new Map());
  // Track which type wells we've already notified about to prevent duplicate notifications
  const notifiedTypeWellsRef = useRef<Set<string>>(new Set());
  // Track the previous product to detect product changes
  const previousProductRef = useRef<string>(currentProduct);
  // Track the previous chart type to detect chart type changes
  const previousChartTypeRef = useRef<EvaChart>(chartType);

  useEffect(() => {
    // Only show toast for specific chart types
    const allowedChartTypes = [
      EvaChart.RateCum,
      EvaChart.RateTime,
      EvaChart.CumTime,
      EvaChart.RateDate,
      EvaChart.TotalRateDate,
      EvaChart.TotalRateCum
    ];

    if (
      !currentProduct ||
      !checkedGlobalTypeWells ||
      checkedGlobalTypeWells.length === 0 ||
      !allowedChartTypes.includes(chartType) ||
      isMultiProductActive
    ) {
      return;
    }

    // Check if the product or chart type changed - if so, clear notifications and re-evaluate all type wells
    const productChanged = previousProductRef.current !== currentProduct;
    const chartTypeChanged = previousChartTypeRef.current !== chartType;
    const contextChanged = productChanged || chartTypeChanged;

    if (contextChanged) {
      // Clear all notifications when product or chart type changes to allow re-notification
      notifiedTypeWellsRef.current.clear();
      previousProductRef.current = currentProduct;
      previousChartTypeRef.current = chartType;
    }

    // Find newly added type wells OR type wells whose arps array changed OR all type wells if product changed
    const currentTypeWellIds = new Set(checkedGlobalTypeWells.map((tw) => tw.uniqueID));
    const newlyAddedTypeWells = checkedGlobalTypeWells.filter((tw) => {
      const isNew = !previousTypeWellIdsRef.current.has(tw.uniqueID);
      const arpsHash = JSON.stringify(
        tw.arps?.map((a) => ({ product: a.product, endDate: a.endDate }))
      );
      const previousHash = previousArpsHashRef.current.get(tw.uniqueID);
      const arpsChanged = previousHash !== arpsHash;

      // If arps changed, clear any previous notifications for this type well
      // This allows re-notification when products are removed and re-added
      if (arpsChanged) {
        const keysToRemove = Array.from(notifiedTypeWellsRef.current).filter((key) =>
          key.startsWith(`${tw.uniqueID}::`)
        );
        keysToRemove.forEach((key) => {
          notifiedTypeWellsRef.current.delete(key);
        });
      }

      // Update the arps hash
      previousArpsHashRef.current.set(tw.uniqueID, arpsHash);

      return isNew || arpsChanged || contextChanged;
    });

    // Update the previous IDs for next comparison
    previousTypeWellIdsRef.current = currentTypeWellIds;

    if (newlyAddedTypeWells.length === 0) {
      return;
    }

    const typeWellsToNotify: string[] = [];

    newlyAddedTypeWells.forEach((typeWell: ICheckedForecast) => {
      const { arps, title, uniqueID } = typeWell;

      if (!arps || arps.length === 0) {
        return;
      }

      const primaryProductName = arps[0]?.product;
      if (!primaryProductName) {
        return;
      }

      const distinctProducts = Array.from(
        new Set(arps.map((segment) => segment.product))
      );

      if (distinctProducts.length < 2) {
        // Need at least 2 distinct products (primary + secondary) to check
        return;
      }

      const primaryEndDate = getLastProductEndDate(arps, primaryProductName);

      if (!primaryEndDate) {
        return;
      }

      const currentProductMatches = getChartProductMatchKeys(currentProduct);

      // Check secondary products (all products except the primary product)
      distinctProducts.forEach((productName) => {
        if (productName.toUpperCase() === primaryProductName.toUpperCase()) {
          return;
        }

        // Check if this secondary product matches the current chart product (case-insensitive)
        if (!currentProductMatches.has(productName.toUpperCase())) {
          return;
        }

        const secondaryEndDate = getLastProductEndDate(arps, productName);

        if (!secondaryEndDate) {
          return;
        }

        // Check if secondary product's end date is after primary product's end date
        if (secondaryEndDate > primaryEndDate) {
          const notificationKey = `${uniqueID}::${productName}`;

          // Only notify if we haven't notified about this type well + product combo
          if (!notifiedTypeWellsRef.current.has(notificationKey)) {
            typeWellsToNotify.push(title || "Type Well");
            notifiedTypeWellsRef.current.add(notificationKey);
          }
        }
      });
    });

    // Show toast if there are type wells to notify about
    if (typeWellsToNotify.length > 0) {
      const notificationMessage =
        typeWellsToNotify.length === 1
          ? `Type Well's ${currentProduct} product trimmed to match primary product end date`
          : `Type Wells' ${currentProduct} product trimmed to match primary product end date`;

      showChartToast(chartId, notificationMessage, ToastType.Info, {
        duration: 5000,
        toastId: `cutoff-${chartId}-${currentProduct}`
      });
    }
  }, [checkedGlobalTypeWells, currentProduct, chartType, chartId, isMultiProductActive]);

  // Reset notifications when type wells change significantly
  useEffect(() => {
    // Clear notification tracking when type wells are removed or changed
    const currentTypeWellIds = new Set(checkedGlobalTypeWells.map((tw) => tw.uniqueID));

    // Remove from previous IDs any type wells that no longer exist
    // This ensures removed type wells are detected as new if re-added
    previousTypeWellIdsRef.current.forEach((typeWellId) => {
      if (!currentTypeWellIds.has(typeWellId)) {
        previousTypeWellIdsRef.current.delete(typeWellId);
        previousArpsHashRef.current.delete(typeWellId);
      }
    });

    // Remove notifications for type wells that no longer exist
    notifiedTypeWellsRef.current.forEach((key) => {
      // Use :: as separator to avoid issues with uniqueIDs containing hyphens
      const typeWellId = key.split("::")[0];
      if (!currentTypeWellIds.has(typeWellId)) {
        notifiedTypeWellsRef.current.delete(key);
      }
    });
  }, [checkedGlobalTypeWells]);

  return null;
}

/**
 * Get the end date from the last segment of an ArpSegment
 */
function getLastProductEndDate(arps: ArpSegment[], productName: string): Date | null {
  if (!arps || !productName) {
    return null;
  }

  let latestDate: Date | null = null;

  arps.forEach((segment) => {
    if (segment.product !== productName || !segment.endDate) {
      return;
    }

    const endDate = new Date(segment.endDate);
    if (isNaN(endDate.getTime())) {
      return;
    }

    if (!latestDate || endDate > latestDate) {
      latestDate = endDate;
    }
  });

  return latestDate;
}

export function getChartProductMatchKeys(currentProduct: string): Set<string> {
  const trimmedProduct = currentProduct.trim();
  const normalizedProduct =
    trimmedProduct === trimmedProduct.toUpperCase()
      ? trimmedProduct
      : getProductType(trimmedProduct)?.key || trimmedProduct;
  const normalizedUpper = normalizedProduct.toUpperCase();

  if (normalizedProduct === ALL_PRODUCT_TYPES.Oil.key) {
    return new Set([
      ProductNames.Oil,
      ProductNames.Condensate,
      ProductNames.CondAbbreviated,
      ProductNames.CondAbbreviatedWithDot
    ]);
  }

  return new Set([normalizedUpper]);
}
